import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ApplicationInstructions from "../../../Components/ApplicationInstructions";
import { getFieldData } from "../../../Services/appService";
import { getCurrentUser, getLoggedUserDetails } from "../../store/auth";
import { useSelector } from "react-redux";
import { getCandidateAppAllTasks } from "../../store/candidate";

const ONBOARDING_STYLES = {
    shell: {
        display: "flex",
        flexDirection: "column",
        gap: "14px"
    },
    hero: {
        borderRadius: "28px",
        padding: "24px 28px",
        background: "linear-gradient(135deg, #f8fbff 0%, #ffffff 60%, #fbfdff 100%)",
        border: "1px solid rgba(191, 219, 254, 0.85)",
        boxShadow: "0 14px 34px rgba(37, 99, 235, 0.06)",
        marginBottom: "18px"
    },
    heroRow: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: "14px",
        width: "100%"
    },
    topRow: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(320px, 0.8fr)",
        gap: "16px",
        alignItems: "stretch",
        width: "100%"
    },
    heroSummary: {
        width: "100%"
    },
    progressHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "12px",
        flexWrap: "wrap",
        marginBottom: "12px"
    },
    progressLabel: {
        fontSize: "14px",
        fontWeight: 700,
        color: "#0f172a"
    },
    progressMeta: {
        fontSize: "13px",
        fontWeight: 700,
        color: "#4353db"
    },
    progressMetaBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        borderRadius: "999px",
        background: "rgba(79, 70, 229, 0.08)",
        border: "1px solid rgba(99, 102, 241, 0.18)"
    },
    progressTrack: {
        position: "relative",
        width: "100%",
        height: "14px",
        borderRadius: "999px",
        background: "linear-gradient(90deg, rgba(191, 219, 254, 0.38) 0%, rgba(224, 231, 255, 0.7) 100%)",
        overflow: "hidden",
        boxShadow: "inset 0 2px 6px rgba(15, 23, 42, 0.08)"
    },
    progressFill: {
        position: "relative",
        height: "100%",
        borderRadius: "999px",
        background: "linear-gradient(90deg, #2563eb 0%, #4f46e5 52%, #7c3aed 100%)",
        boxShadow: "0 10px 24px rgba(79, 70, 229, 0.24)",
        transition: "width 0.2s ease"
    },
    progressMarkers: {
        position: "absolute",
        top: "50%",
        left: "12px",
        right: "12px",
        transform: "translateY(-50%)",
        display: "flex",
        justifyContent: "space-between",
        pointerEvents: "none"
    },
    progressMarker: {
        width: "2px",
        height: "20px",
        borderRadius: "999px",
        background: "rgba(255,255,255,0.55)",
        boxShadow: "0 0 0 1px rgba(99, 102, 241, 0.08)"
    },
    progressStatsRow: {
        marginTop: "10px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "12px",
        flexWrap: "wrap"
    },
    progressCaption: {
        fontSize: "12px",
        lineHeight: 1.5,
        color: "#64748b"
    },
    progressFootnote: {
        fontSize: "12px",
        fontWeight: 700,
        color: "#4338ca"
    },
    statsWrap: {
        display: "flex",
        alignItems: "stretch",
        width: "100%"
    },
    statPill: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "14px",
        padding: "14px 18px",
        borderRadius: "999px",
        background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,255,0.98) 100%)",
        border: "1px solid rgba(191, 219, 254, 0.95)",
        color: "#4353db",
        boxShadow: "0 14px 32px rgba(37, 99, 235, 0.08)",
        width: "100%",
        minWidth: 0,
        minHeight: "84px"
    },
    statPillApproved: {
        border: "1px solid rgba(134, 239, 172, 0.8)",
        background: "linear-gradient(135deg, rgba(240, 253, 244, 0.98) 0%, rgba(236, 253, 245, 0.98) 100%)",
        boxShadow: "0 16px 34px rgba(34, 197, 94, 0.12)"
    },
    statPillPending: {
        border: "1px solid rgba(191, 219, 254, 0.95)"
    },
    statIconWrap: {
        width: "42px",
        height: "42px",
        borderRadius: "999px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#ffffff",
        border: "1px solid rgba(191, 219, 254, 0.95)",
        fontSize: "18px",
        flexShrink: 0
    },
    statIconWrapApproved: {
        color: "#16a34a",
        border: "1px solid rgba(134, 239, 172, 0.85)",
        boxShadow: "0 8px 18px rgba(34, 197, 94, 0.12)",
    },
    statIconWrapPending: {
        color: "#4353db"
    },
    statTextBlock: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        flex: 1
    },
    statSupport: {
        fontSize: "11px",
        fontWeight: 700,
        color: "#64748b",
        letterSpacing: "0.08em",
        textTransform: "uppercase"
    },
    statValue: {
        fontSize: "22px",
        fontWeight: 800,
        color: "#0f172a",
        lineHeight: 1.1
    },
    statLabel: {
        fontSize: "12px",
        lineHeight: 1.45,
        color: "#475569",
        marginTop: "4px"
    },
    priorityCallout: {
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "14px 16px",
        borderRadius: "18px",
        border: "1px solid rgba(251, 191, 36, 0.42)",
        background: "linear-gradient(135deg, rgba(255, 251, 235, 0.98) 0%, rgba(255, 247, 237, 0.98) 100%)",
        boxShadow: "0 12px 24px rgba(245, 158, 11, 0.08)"
    },
    priorityIcon: {
        width: "38px",
        height: "38px",
        borderRadius: "12px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#ffffff",
        color: "#d97706",
        border: "1px solid rgba(251, 191, 36, 0.45)",
        fontSize: "18px",
        flexShrink: 0
    },
    priorityCopy: {
        minWidth: 0,
        flex: 1
    },
    priorityTitle: {
        fontSize: "14px",
        fontWeight: 800,
        color: "#0f172a",
        marginBottom: "2px"
    },
    priorityText: {
        fontSize: "12px",
        lineHeight: 1.55,
        color: "#6b7280",
        margin: 0
    },
    actionRow: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap"
    },
    helpButton: {
        border: "none",
        background: "transparent",
        color: "#4353db",
        fontSize: "14px",
        fontWeight: 700,
        padding: 0
    },
    tabsWrap: {
        borderRadius: "26px",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
        border: "1px solid rgba(226, 232, 240, 1)",
        boxShadow: "0 14px 36px rgba(15, 23, 42, 0.05)",
        overflow: "visible"
    },
    tabsRail: {
        position: "relative",
        padding: "10px 52px 10px 52px",
        overflow: "visible"
    },
    tabsScroller: {
        overflowX: "auto",
        overflowY: "visible",
        width: "100%",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        display: "flex",
        alignItems: "center"
    },
    tabsList: {
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        minWidth: "100%",
        width: "max-content",
        whiteSpace: "nowrap"
    },
    tabItemWrap: {
        position: "relative",
        display: "inline-flex",
        alignItems: "stretch",
        overflow: "visible"
    },
    tabTitleWrap: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        whiteSpace: "nowrap",
        flexWrap: "nowrap",
        position: "relative"
    },
    tabButton: {
        border: "none",
        borderBottom: "none",
        borderRadius: "16px 16px 0 0",
        background: "transparent",
        color: "#334155",
        padding: "15px 20px 13px 20px",
        minHeight: "58px",
        fontSize: "14px",
        fontWeight: 700,
        lineHeight: 1.2,
        boxShadow: "none",
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "nowrap",
        position: "relative",
        transition: "background 0.18s ease, color 0.18s ease, border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease",
        flex: "0 0 auto",
        overflow: "visible"
    },
    activeTabButton: {
        color: "#0f172a",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
        borderBottom: "3px solid #4f46e5",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), 0 8px 20px rgba(79, 70, 229, 0.08)",
        zIndex: 2
    },
    tabLabel: {
        fontSize: "15px",
        fontWeight: 700
    },
    tabIcon: {
        color: "#475569",
        width: "18px",
        textAlign: "center",
        fontSize: "15px"
    },
    activeTabIcon: {
        color: "#4f46e5"
    },
    stepStateIcon: {
        marginLeft: "4px",
        fontSize: "22px",
        opacity: 0.95
    },
    tabTooltip: {
        position: "fixed",
        transform: "translate(-50%, calc(-100% - 12px))",
        minWidth: "220px",
        maxWidth: "280px",
        padding: "12px 14px",
        borderRadius: "16px",
        background: "linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)",
        border: "1px solid rgba(148, 163, 184, 0.2)",
        boxShadow: "0 18px 38px rgba(15, 23, 42, 0.28)",
        color: "#e2e8f0",
        textAlign: "left",
        whiteSpace: "normal",
        zIndex: 5,
        pointerEvents: "none",
        opacity: 0,
        visibility: "hidden",
        transition: "opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease"
    },
    tabTooltipVisible: {
        opacity: 1,
        visibility: "visible",
        transform: "translate(-50%, calc(-100% - 16px))"
    },
    tabTooltipArrow: {
        position: "absolute",
        left: "50%",
        bottom: "-7px",
        width: "14px",
        height: "14px",
        background: "#1e293b",
        borderRight: "1px solid rgba(148, 163, 184, 0.2)",
        borderBottom: "1px solid rgba(148, 163, 184, 0.2)",
        transform: "translateX(-50%) rotate(45deg)"
    },
    tabTooltipEyebrow: {
        fontSize: "10px",
        fontWeight: 800,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: "#93c5fd",
        marginBottom: "6px"
    },
    tabTooltipTitle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "10px",
        fontSize: "13px",
        fontWeight: 800,
        color: "#f8fafc",
        marginBottom: "6px"
    },
    tabTooltipStatus: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 8px",
        borderRadius: "999px",
        fontSize: "10px",
        fontWeight: 800,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        flexShrink: 0
    },
    tabTooltipStatusDone: {
        background: "rgba(34, 197, 94, 0.16)",
        color: "#86efac"
    },
    tabTooltipStatusPending: {
        background: "rgba(251, 191, 36, 0.16)",
        color: "#fcd34d"
    },
    tabTooltipText: {
        fontSize: "12px",
        lineHeight: 1.5,
        color: "#cbd5e1",
        margin: 0
    },
    scrollButton: {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        width: "38px",
        height: "38px",
        borderRadius: "999px",
        border: "1px solid rgba(203, 213, 225, 1)",
        background: "#ffffff",
        color: "#475569",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 10px 20px rgba(15, 23, 42, 0.08)",
        zIndex: 3
    },
    scrollButtonLeft: {
        left: "12px"
    },
    scrollButtonRight: {
        right: "12px"
    }
};

export default function OnboardingSteps() {
    const [instructionShowHide, setInstructionShowHide] = useState(false);
    const [entityType, setEntityType] = useState("");
    const [appStatus, setAppStatus] = useState(false);
    const [appApproved, setAppApproved] = useState(false);
    const [appAllTasks, setAllAppTasks] = useState([]);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [tooltipState, setTooltipState] = useState(null);

    const tabsNavRef = useRef(null);

    const location = useLocation();
    const appType = location?.pathname;

    const navigate = useNavigate();
    const currentUser = getCurrentUser();
    const userDetailsSelector = useSelector(getLoggedUserDetails);
    const candidateAppAllTasksSelector = useSelector(getCandidateAppAllTasks);

    useEffect(() => {
        if (userDetailsSelector) {
            const entType = getFieldData(
                userDetailsSelector.performerData,
                "candidateonboardingform",
                "CandidateEntity"
            );
            setEntityType(entType);
            setAppStatus(userDetailsSelector?.appSubmit);
            setAppApproved(userDetailsSelector?.approved > 0);
        }
    }, [userDetailsSelector]);

    useEffect(() => {
        setAllAppTasks(candidateAppAllTasksSelector);
    }, [candidateAppAllTasksSelector]);

    const handleInstructionShowHide = () => {
        setInstructionShowHide(!instructionShowHide);
    };

    const handleSelect = (key) => {
        navigate(key);
    };

    const updateScrollState = () => {
        const tabsNav = tabsNavRef.current;
        if (!tabsNav) return;

        const { scrollLeft, scrollWidth, clientWidth } = tabsNav;
        setCanScrollLeft(scrollLeft > 4);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
    };

    const handleTabsScroll = (direction) => {
        const tabsNav = tabsNavRef.current;
        if (!tabsNav) return;

        const amount = Math.max(220, Math.floor(tabsNav.clientWidth * 0.55));
        tabsNav.scrollBy({
            left: direction === "left" ? -amount : amount,
            behavior: "smooth"
        });
    };

    const getStepIcon = (isDone) => (
        isDone
            ? <i className="fa fa-check-circle text-success ml-1"></i>
            : <i className="fa fa-clock-o text-warning ml-1"></i>
    );

    const getStepMenuIconClass = (stepKey) => {
        switch (stepKey) {
            case "/Onboarding":
            case "/DynamicForm":
                return "fa-file-text-o";
            case "/ContractDocs":
                return "fa-folder-open-o";
            case "/AchInfo":
                return "fa-university";
            case "/BackgroundChecks":
                return "fa-search";
            case "/OnboardingStatus":
                return "fa-clipboard";
            case "/ReportingDetails":
                return "fa-map-marker";
            case "/TimeLogs":
                return "fa-clock-o";
            case "/Compliance":
                return "fa-shield";
            case "/CustomReport":
                return "fa-bar-chart";
            default:
                return "fa-circle-o";
        }
    };

    const getStepStatusTooltip = (label, isDone, isActive) => {
        const statusText = isDone ? "Completed" : "Pending";
        const detailText = isActive
            ? `${label} is currently open in this workspace.`
            : `Open ${label} to review or complete this step.`;

        return {
            statusText,
            detailText
        };
    };

    const showTabTooltip = (event, tab, isActive) => {
        const tooltipContent = getStepStatusTooltip(tab.label, tab.done, isActive);
        const rect = event.currentTarget.getBoundingClientRect();

        setTooltipState({
            key: tab.key,
            left: rect.left + (rect.width / 2),
            top: rect.top,
            label: tab.label,
            done: tab.done,
            statusText: tooltipContent.statusText,
            detailText: tooltipContent.detailText
        });
    };

    const hideTabTooltip = (tabKey) => {
        setTooltipState((currentTooltip) => (
            currentTooltip && currentTooltip.key === tabKey ? null : currentTooltip
        ));
    };

    const renderTabTitle = (eventKey, label, isDone, isActive) => (
        <span style={ONBOARDING_STYLES.tabTitleWrap}>
            <i
                className={`fa ${getStepMenuIconClass(eventKey)}`}
                aria-hidden="true"
                style={{
                    ...ONBOARDING_STYLES.tabIcon,
                    ...(isActive ? ONBOARDING_STYLES.activeTabIcon : {})
                }}
            ></i>
            <span style={ONBOARDING_STYLES.tabLabel}>{label}</span>
            <span style={ONBOARDING_STYLES.stepStateIcon}>{getStepIcon(isDone)}</span>
        </span>
    );

    const allPaths = ["/Onboarding", "/ContractDocs", "/AchInfo", "/BackgroundChecks", "/ReportingDetails",
        "/OnboardingStatus", "/DynamicForm", "/TimeLogs", "/CustomReport", "/Compliance"];

    useEffect(() => {
        const tabsNav = tabsNavRef.current;
        if (!tabsNav) return;

        const rafId = window.requestAnimationFrame(() => {
            updateScrollState();
        });

        const handleResize = () => updateScrollState();
        const handleScroll = () => updateScrollState();
        let resizeObserver = null;

        tabsNav.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleResize);

        if (typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(() => updateScrollState());
            resizeObserver.observe(tabsNav);
        }

        return () => {
            window.cancelAnimationFrame(rafId);
            tabsNav.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
            if (resizeObserver) resizeObserver.disconnect();
        };
    }, [appType, appApproved, appStatus, entityType, appAllTasks]);

    useEffect(() => {
        const tabsNav = tabsNavRef.current;
        if (!tabsNav) return;

        const activeTab = tabsNav.querySelector('[data-active="true"]');
        if (activeTab && typeof activeTab.scrollIntoView === "function") {
            activeTab.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        }
    }, [appType]);

    if (allPaths.indexOf(appType) > -1) {
        const activePath = appType === "/DynamicForm" ? "/Onboarding" : appType;
        const timeLogDone = appAllTasks.filter((x) => x.loanPhase_Desc === "Time Log").length > 0;
        const complianceDone = appAllTasks.filter((x) => x.loanPhase_Desc === "Compliance").length > 0;

        const tabItems = [
            { key: "/Onboarding", label: "Onboarding Form", done: appStatus },
            { key: "/ContractDocs", label: "Contract Documents", done: appApproved },
            ...(entityType !== "C2C Basis" ? [{ key: "/AchInfo", label: "ACH", done: appApproved }] : []),
            { key: "/BackgroundChecks", label: "Background Check", done: appApproved },
            { key: "/OnboardingStatus", label: "Onboarding Status", done: appApproved },
            { key: "/ReportingDetails", label: "Reporting Details", done: appApproved },
            { key: "/TimeLogs", label: entityType !== "C2C Basis" ? "Time Log and Payments" : "Time Log", done: timeLogDone },
            { key: "/Compliance", label: "Compliance", done: complianceDone },
            ...(entityType !== "C2C Basis" ? [{ key: "/CustomReport", label: "Reports", done: timeLogDone }] : [])
        ];

        const visibleStepCount = tabItems.length;
        const completedStepCount = tabItems.filter((tab) => tab.done).length;
        const incompleteStepCount = Math.max(visibleStepCount - completedStepCount, 0);
        const overallProgress = visibleStepCount > 0
            ? Math.round((completedStepCount / visibleStepCount) * 100)
            : 0;
        const onboardingFormStep = tabItems.find((tab) => tab.key === "/Onboarding");
        const isOnboardingFormComplete = onboardingFormStep ? onboardingFormStep.done : false;

        return <React.Fragment>
            <style>{`
                [data-onboarding-tabs-scroller="true"]::-webkit-scrollbar {
                    display: none;
                }
            `}</style>

            <div style={ONBOARDING_STYLES.hero}>
                <div style={ONBOARDING_STYLES.heroRow}>
                    <div style={ONBOARDING_STYLES.topRow}>
                        <div style={ONBOARDING_STYLES.heroSummary}>
                            <div style={ONBOARDING_STYLES.progressHeader}>
                                <span style={ONBOARDING_STYLES.progressLabel}>Overall progress</span>
                                <span style={{ ...ONBOARDING_STYLES.progressMeta, ...ONBOARDING_STYLES.progressMetaBadge }}>
                                    <i className="fa fa-flag-checkered" aria-hidden="true"></i>
                                    {overallProgress}% complete
                                </span>
                            </div>
                            <div style={ONBOARDING_STYLES.progressTrack}>
                                <div
                                    style={{
                                        ...ONBOARDING_STYLES.progressFill,
                                        width: `${overallProgress}%`
                                    }}
                                ></div>
                                <div style={ONBOARDING_STYLES.progressMarkers} aria-hidden="true">
                                    <span style={ONBOARDING_STYLES.progressMarker}></span>
                                    <span style={ONBOARDING_STYLES.progressMarker}></span>
                                    <span style={ONBOARDING_STYLES.progressMarker}></span>
                                </div>
                            </div>
                            <div style={ONBOARDING_STYLES.progressStatsRow}>
                                <div style={ONBOARDING_STYLES.progressCaption}>
                                    {completedStepCount} of {visibleStepCount} visible onboarding steps are complete.
                                </div>
                                <div style={ONBOARDING_STYLES.progressFootnote}>
                                    {incompleteStepCount === 0
                                        ? "Everything visible is finished"
                                        : `${incompleteStepCount} step${incompleteStepCount === 1 ? "" : "s"} remaining`}
                                </div>
                            </div>
                        </div>
                        <div style={ONBOARDING_STYLES.statsWrap}>
                            <span
                                style={{
                                    ...ONBOARDING_STYLES.statPill,
                                    ...(appApproved ? ONBOARDING_STYLES.statPillApproved : ONBOARDING_STYLES.statPillPending)
                                }}
                            >
                                <span
                                    style={{
                                        ...ONBOARDING_STYLES.statIconWrap,
                                        ...(appApproved ? ONBOARDING_STYLES.statIconWrapApproved : ONBOARDING_STYLES.statIconWrapPending)
                                    }}
                                >
                                    <i className={`fa ${appApproved ? "fa-check-circle" : "fa-clock-o"}`} aria-hidden="true"></i>
                                </span>
                                <span style={ONBOARDING_STYLES.statTextBlock}>
                                    <span style={ONBOARDING_STYLES.statSupport}>Onboarding Status</span>
                                    <span style={ONBOARDING_STYLES.statValue}>{appApproved ? "Approved" : "In Progress"}</span>
                                    <span style={ONBOARDING_STYLES.statLabel}>
                                        {appApproved
                                            ? "Your onboarding has cleared review and is ready for the next stage."
                                            : "Complete the remaining onboarding items to move into approval."}
                                    </span>
                                </span>
                            </span>
                        </div>
                    </div>

                    {!isOnboardingFormComplete && (
                        <div style={ONBOARDING_STYLES.priorityCallout}>
                            <span style={ONBOARDING_STYLES.priorityIcon}>
                                <i className="fa fa-bolt" aria-hidden="true"></i>
                            </span>
                            <div style={ONBOARDING_STYLES.priorityCopy}>
                                <div style={ONBOARDING_STYLES.priorityTitle}>Onboarding Form is the top priority</div>
                                <p style={ONBOARDING_STYLES.priorityText}>
                                    Please complete the Onboarding Form as soon as possible. It is the most important step and unlocks the rest of the onboarding workflow.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div style={ONBOARDING_STYLES.shell}>
                <div style={ONBOARDING_STYLES.actionRow}>
                    <button
                        type="button"
                        style={ONBOARDING_STYLES.helpButton}
                        onClick={handleInstructionShowHide}
                    >
                        <i className="fa fa-hand-o-right text-dark mr-2"></i>
                        Help for completing onboarding process
                    </button>
                </div>

                <div style={ONBOARDING_STYLES.tabsWrap}>
                    <div style={ONBOARDING_STYLES.tabsRail}>
                        <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                                ...ONBOARDING_STYLES.scrollButton,
                                ...ONBOARDING_STYLES.scrollButtonLeft,
                                opacity: canScrollLeft ? 1 : 0.35,
                                pointerEvents: canScrollLeft ? "auto" : "none"
                            }}
                            onClick={() => handleTabsScroll("left")}
                            aria-label="Scroll tabs left"
                        >
                            <i className="fa fa-angle-left" aria-hidden="true"></i>
                        </button>

                        <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                                ...ONBOARDING_STYLES.scrollButton,
                                ...ONBOARDING_STYLES.scrollButtonRight,
                                opacity: canScrollRight ? 1 : 0.35,
                                pointerEvents: canScrollRight ? "auto" : "none"
                            }}
                            onClick={() => handleTabsScroll("right")}
                            aria-label="Scroll tabs right"
                        >
                            <i className="fa fa-angle-right" aria-hidden="true"></i>
                        </button>

                        <div ref={tabsNavRef} style={ONBOARDING_STYLES.tabsScroller} data-onboarding-tabs-scroller="true">
                            <div id="onboarding-step-tabs" style={ONBOARDING_STYLES.tabsList}>
                                {tabItems.map((tab) => {
                                    const isActive = tab.key === activePath;
                                    return (
                                        <div
                                            key={tab.key}
                                            onMouseEnter={(event) => showTabTooltip(event, tab, isActive)}
                                            onMouseLeave={() => hideTabTooltip(tab.key)}
                                            onFocus={(event) => showTabTooltip(event, tab, isActive)}
                                            onBlur={() => hideTabTooltip(tab.key)}
                                            style={ONBOARDING_STYLES.tabItemWrap}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => handleSelect(tab.key)}
                                                data-active={isActive ? "true" : "false"}
                                                style={{
                                                    ...ONBOARDING_STYLES.tabButton,
                                                    ...(isActive ? ONBOARDING_STYLES.activeTabButton : {})
                                                }}
                                            >
                                                {renderTabTitle(tab.key, tab.label, tab.done, isActive)}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {instructionShowHide && (
                    <ApplicationInstructions
                        instructionShowHide={instructionShowHide}
                        handleInstructionShowHide={handleInstructionShowHide}
                        entityType={entityType}
                    ></ApplicationInstructions>
                )}

                {tooltipState && (
                    <span
                        role="tooltip"
                        style={{
                            ...ONBOARDING_STYLES.tabTooltip,
                            ...ONBOARDING_STYLES.tabTooltipVisible,
                            left: `${tooltipState.left}px`,
                            top: `${tooltipState.top}px`
                        }}
                    >
                        <span style={ONBOARDING_STYLES.tabTooltipEyebrow}>Onboarding Step</span>
                        <span style={ONBOARDING_STYLES.tabTooltipTitle}>
                            <span>{tooltipState.label}</span>
                            <span
                                style={{
                                    ...ONBOARDING_STYLES.tabTooltipStatus,
                                    ...(tooltipState.done ? ONBOARDING_STYLES.tabTooltipStatusDone : ONBOARDING_STYLES.tabTooltipStatusPending)
                                }}
                            >
                                <i className={`fa ${tooltipState.done ? "fa-check-circle" : "fa-clock-o"}`} aria-hidden="true"></i>
                                {tooltipState.statusText}
                            </span>
                        </span>
                        <span style={ONBOARDING_STYLES.tabTooltipText}>{tooltipState.detailText}</span>
                        <span style={ONBOARDING_STYLES.tabTooltipArrow}></span>
                    </span>
                )}
            </div>
        </React.Fragment>;
    }

    return "";
}
